import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Save,
  Loader2,
  Package,
  MapPin,
  Calendar,
  Camera,
  Download,
  X,
  LogOut,
  Check,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../lib/api';
import type { Purchase, DownloadLink } from '../types';

/* ------------------------------------------------------------------ */
/*  Download modal (re-used pattern from SurferSearch)                 */
/* ------------------------------------------------------------------ */

function DownloadModal({
  links,
  onClose,
}: {
  links: DownloadLink[];
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-8 relative max-h-[80vh] overflow-y-auto mx-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="text-xl font-bold mb-2">Download Files</h2>
        <p className="text-slate-500 text-sm mb-6">
          Your unwatermarked, full-resolution files are ready. Links expire in 1 hour.
        </p>
        <div className="space-y-3">
          {links.map(link => (
            <a
              key={link.photoId}
              href={link.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors"
            >
              <Download className="h-5 w-5 text-blue-600 shrink-0" />
              <span className="truncate text-sm font-medium">{link.filename}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Profile page                                                       */
/* ------------------------------------------------------------------ */

export default function Profile() {
  const { user, token, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  // ---- Edit form state ----
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ---- Purchases state ----
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loadingPurchases, setLoadingPurchases] = useState(true);

  // ---- Download modal ----
  const [downloadLinks, setDownloadLinks] = useState<DownloadLink[] | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!user || !token) {
      navigate('/');
    }
  }, [user, token, navigate]);

  // Sync form with user data
  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
    }
  }, [user]);

  // Fetch purchase history
  const fetchPurchases = useCallback(async () => {
    if (!token) return;
    setLoadingPurchases(true);
    try {
      const res = await fetch(apiUrl('/api/photos/purchases'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPurchases(data.purchases ?? []);
      }
    } catch {
      // silent
    } finally {
      setLoadingPurchases(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  // ---- Handlers ----
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const res = await fetch(apiUrl('/api/auth/profile'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name, email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      updateUser({ name: data.user.name, email: data.user.email });
      setSaveMessage({ type: 'success', text: 'Profile updated' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err: unknown) {
      setSaveMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Something went wrong',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRedownload = async (purchaseId: string) => {
    if (!token) return;
    setDownloadingId(purchaseId);
    try {
      const res = await fetch(apiUrl('/api/photos/download'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ purchaseId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.downloadUrls?.length) {
          setDownloadLinks(data.downloadUrls);
        }
      }
    } catch {
      // silent
    } finally {
      setDownloadingId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <>
      {downloadLinks && (
        <DownloadModal links={downloadLinks} onClose={() => setDownloadLinks(null)} />
      )}

      <div className="space-y-10 max-w-3xl mx-auto">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
              My Profile
            </h1>
            <p className="text-slate-500 mt-1">
              Manage your account and view purchase history
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Log Out
          </button>
        </div>

        {/* ---- Profile info card ---- */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              Account Information
            </h2>
          </div>
          <form onSubmit={handleSave} className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Role
                </label>
                <input
                  type="text"
                  value={user.role === 'SURFER' ? 'Surfer' : 'Photographer'}
                  disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Member Since
                </label>
                <input
                  type="text"
                  value="--"
                  disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 text-sm"
                />
              </div>
            </div>

            {/* Save feedback */}
            {saveMessage && (
              <div
                className={`flex items-center gap-2 text-sm p-3 rounded-lg ${
                  saveMessage.type === 'success'
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {saveMessage.type === 'success' && <Check className="h-4 w-4" />}
                {saveMessage.text}
              </div>
            )}

            <button
              type="submit"
              disabled={saving || (name === user.name && email === user.email)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </section>

        {/* ---- Purchase history ---- */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-600" />
              Purchase History
            </h2>
          </div>

          {loadingPurchases ? (
            <div className="p-12 text-center text-slate-400">
              <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin opacity-60" />
              <p className="text-sm">Loading purchases...</p>
            </div>
          ) : purchases.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Camera className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No purchases yet</p>
              <p className="text-sm mt-1">
                Browse surf photos and buy your first pack!
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {purchases.map(purchase => (
                <div key={purchase._id} className="p-5 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: purchase info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900 truncate">
                          {purchase.pack
                            ? purchase.pack.name
                            : `${purchase.photos.length} photo${purchase.photos.length !== 1 ? 's' : ''}`}
                        </span>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            purchase.purchaseType === 'pack'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {purchase.purchaseType === 'pack' ? 'Pack' : 'Individual'}
                        </span>
                      </div>

                      {/* Photo thumbnails */}
                      {purchase.photos.length > 0 && (
                        <div className="flex items-center gap-2 mt-2.5 mb-2">
                          {purchase.photos.slice(0, 5).map(photo => (
                            <div
                              key={photo._id}
                              className="h-12 w-12 rounded-lg bg-slate-100 overflow-hidden shrink-0"
                            >
                              {photo.mediaType === 'image' ? (
                                <img
                                  src={photo.watermarkedUrl}
                                  alt={photo.filename}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Camera className="h-4 w-4 text-slate-300" />
                                </div>
                              )}
                            </div>
                          ))}
                          {purchase.photos.length > 5 && (
                            <span className="text-xs text-slate-400 font-medium">
                              +{purchase.photos.length - 5} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Meta row */}
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        {purchase.photos[0]?.beach && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {purchase.photos[0].beach}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(purchase.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Right: price + re-download */}
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-slate-900">
                        ${purchase.amount.toFixed(2)}
                      </p>
                      <button
                        onClick={() => handleRedownload(purchase._id)}
                        disabled={downloadingId === purchase._id}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
                      >
                        {downloadingId === purchase._id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                        Re-download
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
